/**
 * The Android shell, reached without depending on it.
 *
 * The plugin's own package is a dependency of `mobile/`, not of this app, and
 * importing it here would put Capacitor into the browser bundle that all 39
 * people load — to serve a feature only the handful on company phones can use.
 * Capacitor injects its bridge into whatever page the WebView loads, so the
 * plugin is reachable on `window` at runtime and typed by hand here instead.
 *
 * Everything below returns null or does nothing in a browser, which is what
 * makes it safe to call unconditionally from shared components.
 */

/** One position, as the plugin reports it. */
export interface NativePosition {
  latitude: number;
  longitude: number;
  accuracy: number | null;
  /** Milliseconds since the epoch, from the device's own clock. */
  time: number | null;
}

interface WatcherOptions {
  /** Setting this is what makes Android run a foreground service. */
  backgroundMessage: string;
  backgroundTitle: string;
  requestPermissions: boolean;
  /** Whether a cached fix may be delivered before a fresh one arrives. */
  stale: boolean;
  distanceFilter: number;
}

interface BackgroundGeolocationPlugin {
  addWatcher(
    options: WatcherOptions,
    callback: (position: NativePosition | undefined, error?: { code?: string }) => void,
  ): Promise<string>;
  removeWatcher(options: { id: string }): Promise<void>;
  openSettings(): Promise<void>;
}

/** A listener handle, as every Capacitor plugin returns one. */
export interface NativeListener {
  remove(): Promise<void>;
}

/**
 * The subset of `@capacitor/app` this app uses.
 *
 * Only the hardware back button and minimising. Not `addListener('appUrlOpen')`
 * or the rest of the surface, because typing what is not called invites calling
 * it against a shell that may not have the plugin.
 */
interface AppPlugin {
  /**
   * Returns the handle **synchronously**, not a promise.
   *
   * Capacitor's injected proxy calls `Capacitor.addListener` directly, and the
   * bridge hands back a plain `{ remove }` object. The `@capacitor/app` package
   * would return a promise, but nothing imports it here — the plugin is reached
   * through `window.Capacitor`. Typing this as a promise is what made a `.then`
   * call compile and then throw a TypeError on a handset, so the union is
   * declared and callers must handle both.
   */
  addListener(
    event: 'backButton',
    handler: (event: { canGoBack: boolean }) => void,
  ): NativeListener | Promise<NativeListener>;
  /*
    These are declared as promises because they genuinely are: a @PluginMethod
    compiles to `Capacitor.nativePromise(...)` in the injected proxy, unlike
    addListener above. Optional because an older shell exposes neither, and
    calling a missing method is the same class of crash.
  */
  minimizeApp?(): Promise<void>;
  exitApp?(): Promise<void>;
  /** `build` is the versionCode as a string; `version` is the versionName. */
  getInfo?(): Promise<{ name: string; id: string; build: string; version: string }>;
}

/**
 * The subset of `@capacitor/browser` this app uses: one call, to hand a URL to
 * the system browser (a Chrome Custom Tab on Android).
 *
 * It exists because the WebView cannot download a spreadsheet or render a PDF
 * on its own — a file link that works in Chrome does nothing inside the shell.
 * Chrome, opened over the top, downloads what it cannot show and renders what
 * it can, which is what "open the file" should do on a phone.
 */
interface BrowserPlugin {
  open(options: { url: string }): Promise<void>;
}

/**
 * The subset of `@capacitor/local-notifications` this app uses: ask once, show
 * one, and hear it tapped.
 *
 * It exists because a WebView in the background makes no sound and shows
 * nothing: a chat message that arrives while the phone is in a pocket is a
 * badge nobody sees until they open the app. A notification in the tray is
 * what a phone does about that. Scheduled "now" from the page's own poll —
 * there is no push, so this only fires while the shell is alive, which the
 * location foreground service keeps it for the technicians it matters to.
 */
interface LocalNotificationsPlugin {
  checkPermissions(): Promise<{ display: string }>;
  requestPermissions(): Promise<{ display: string }>;
  /**
   * A channel of our own, created before the first notice.
   *
   * The plugin's built-in "default" channel is IMPORTANCE_DEFAULT, which makes
   * a sound but never a banner over what you are doing — and a channel's
   * importance cannot be raised after Android has registered it, so the only
   * way to a Messenger-like heads-up is a second channel at importance 4.
   * Creating one that already exists is a no-op, so this is safe every launch.
   */
  createChannel?(options: {
    id: string;
    name: string;
    description?: string;
    importance?: number;
    visibility?: number;
    vibration?: boolean;
  }): Promise<void>;
  schedule(options: {
    notifications: Array<{
      id: number;
      title: string;
      body: string;
      /** Which channel carries it; ours, not the plugin's "default". */
      channelId?: string;
      /** Route to open when tapped; read back from `actionPerformed`. */
      extra?: { link?: string };
    }>;
  }): Promise<unknown>;
  /** Takes a notice back out of the tray — the chat was read somewhere else. */
  cancel(options: { notifications: Array<{ id: number }> }): Promise<void>;
  /** Same sync-or-promise shape as `App.addListener`, for the same reason. */
  addListener(
    event: 'localNotificationActionPerformed',
    handler: (event: { notification: { extra?: { link?: string } } }) => void,
  ): NativeListener | Promise<NativeListener>;
}

/**
 * The subset of `@capawesome/capacitor-badge` this app uses: a number on the
 * launcher icon, or none. Whether the launcher shows numbers at all is the
 * launcher's business (Samsung and Xiaomi do; stock Pixel shows a dot from
 * the notification instead), so every call here is best-effort.
 */
interface BadgePlugin {
  set(options: { count: number }): Promise<void>;
  clear(): Promise<void>;
  isSupported(): Promise<{ isSupported: boolean }>;
}

/**
 * The subset of `@capacitor/push-notifications` this app uses.
 *
 * Push is the only way a message reaches a handset whose app has been closed:
 * the poll lives in the WebView, and Android destroys that the moment the app
 * is swiped away. Google's own service holds the delivery instead, and wakes
 * the phone whether or not this app is running.
 *
 * The app never renders these itself — a `notification` message is drawn by
 * Android — so the only listeners here are the one that hands the address to
 * the server and the one that hears a notice being tapped.
 */
interface PushNotificationsPlugin {
  checkPermissions(): Promise<{ receive: string }>;
  requestPermissions(): Promise<{ receive: string }>;
  /** Asks Google for this installation's address; answers on `registration`. */
  register(): Promise<void>;
  addListener(
    event: 'registration',
    handler: (token: { value: string }) => void,
  ): NativeListener | Promise<NativeListener>;
  addListener(
    event: 'registrationError',
    handler: (error: { error: string }) => void,
  ): NativeListener | Promise<NativeListener>;
  addListener(
    event: 'pushNotificationActionPerformed',
    handler: (action: { notification: { data?: Record<string, string> } }) => void,
  ): NativeListener | Promise<NativeListener>;
}

interface CapacitorGlobal {
  isNativePlatform?: () => boolean;
  getPlatform?: () => string;
  Plugins?: {
    BackgroundGeolocation?: BackgroundGeolocationPlugin;
    App?: AppPlugin;
    Browser?: BrowserPlugin;
    LocalNotifications?: LocalNotificationsPlugin;
    Badge?: BadgePlugin;
    PushNotifications?: PushNotificationsPlugin;
  };
}

function capacitor(): CapacitorGlobal | null {
  const global = (window as unknown as { Capacitor?: CapacitorGlobal }).Capacitor;
  return global ?? null;
}

/** True only inside the Android shell, never in a browser tab. */
export function isNativeApp(): boolean {
  return capacitor()?.isNativePlatform?.() === true;
}

/**
 * The background geolocation plugin, or null.
 *
 * Null in a browser, and also null in a shell built before the plugin was
 * added — an older APK on somebody's phone is a real thing to survive, not a
 * case to crash on.
 */
export function backgroundGeolocation(): BackgroundGeolocationPlugin | null {
  return capacitor()?.Plugins?.BackgroundGeolocation ?? null;
}

/**
 * The App plugin, or null.
 *
 * Null in a browser, and null in a shell built before `@capacitor/app` was
 * added — which is every APK issued up to version 1.1. Those keep the old
 * behaviour (back closes the app) rather than breaking, which is the same
 * survive-an-older-shell rule the geolocation accessor follows.
 */
export function nativeApp(): AppPlugin | null {
  return capacitor()?.Plugins?.App ?? null;
}

/**
 * The Browser plugin, or null.
 *
 * Null in a browser (where a plain link already opens a new tab) and null in a
 * shell built before `@capacitor/browser` was added — every APK up to v1.2.
 * Those keep opening file links in the WebView, which is the pre-existing
 * behaviour, not a new crash.
 */
export function nativeBrowser(): BrowserPlugin | null {
  return capacitor()?.Plugins?.Browser ?? null;
}

/**
 * The local notifications plugin, or null.
 *
 * Null in a browser and in every shell up to v1.3, which were built without
 * it. Those keep the in-page sound and vibration and simply show nothing in
 * the tray — the pre-existing behaviour, not a crash.
 */
export function nativeNotifications(): LocalNotificationsPlugin | null {
  return capacitor()?.Plugins?.LocalNotifications ?? null;
}

/** The launcher badge plugin, or null — same rule as the notifications. */
export function nativeBadge(): BadgePlugin | null {
  return capacitor()?.Plugins?.Badge ?? null;
}

/**
 * The push plugin, or null.
 *
 * Null in a browser and in every shell up to v1.5. Those keep the poll's own
 * alerts, which reach them whenever the app is running — they simply hear
 * nothing while it is closed, which is the behaviour they have always had.
 */
export function nativePush(): PushNotificationsPlugin | null {
  return capacitor()?.Plugins?.PushNotifications ?? null;
}
